import { focusManager } from '@tanstack/react-query';

/**
 * Polling runs only while someone is using the workspace.
 *
 * React Query already skips interval refetches while the tab is hidden. A tab left
 * open on a desk stays visible, so without this every poll in the shell would keep
 * reading the server around the clock. After IDLE_AFTER_MS without input the
 * workspace counts as unfocused and polls pause; the next input counts as focus, so
 * every stale query reads again at once. Two kinds of polls opt out on a visible tab:
 * badges keep reading slowly (forAttentionCue) and runs someone may be watching keep
 * their pace (whileVisible).
 *
 * Hiding the tab for less than AWAY_AFTER_MS is not leaving it. A glance elsewhere or
 * a pane switched back and forth would otherwise read every stale query each time.
 */
export const AWAY_AFTER_MS = 30 * 1000;
export const IDLE_AFTER_MS = 10 * 60 * 1000;
// What tells someone something needs them still reads, this often, on a visible tab nobody uses.
export const IDLE_CUE_MS = 5 * 60 * 1000;
const INPUT_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'];
// pointermove fires constantly; one timer reset a second is enough.
const INPUT_THROTTLE_MS = 1000;

let noteInput = () => {};
// 'active', 'idle' (visible, unused) or 'hidden'. Active until the watcher is installed.
let attention = 'active';

/**
 * Interval for a run someone may watch without touching anything, such as setup, an
 * approved change being applied or an agent at work: it keeps reading while the tab is
 * visible. Pass the result as `refetchInterval` with `refetchIntervalInBackground: true`.
 */
export const whileVisible = (ms) => (ms && attention !== 'hidden' ? ms : false);

/** Interval for badges such as Needs you: slower, but still reading, on a visible tab nobody uses. */
export const forAttentionCue = (ms) => {
  if (attention === 'hidden') return false;
  return attention === 'idle' ? Math.max(ms, IDLE_CUE_MS) : ms;
};

/** Interval for everything else, which only reads while someone uses the workspace. */
export const whileInUse = (ms) => (ms && attention === 'active' ? ms : false);

/**
 * `retry` for a query that reads in the background. React Query holds a retry until the
 * workspace is focused, and later ticks wait on it, so a failed read on a visible idle
 * tab would freeze the poll. Retry only while someone uses the workspace; otherwise
 * the next tick reads afresh. `retry` is a count or React Query's retry function.
 */
export const retryWhileInUse = (retry = 3) => (count, error) => (
  attention === 'active' && (typeof retry === 'function' ? retry(count, error) : count < retry)
);

/** Input the page's own listeners cannot see, such as clicks inside a View frame. */
export function noteUserActivity() {
  noteInput();
}

/**
 * Reports `setFocused(true)` while the tab is visible and was used within
 * `idleAfterMs`, and `setFocused(false)` otherwise, once it has stayed hidden for
 * `awayAfterMs`; calls `onResume` when such a tab shows again or someone uses an idle
 * one. Returns a cleanup function.
 */
export function watchAttention(setFocused, {
  idleAfterMs = IDLE_AFTER_MS, awayAfterMs = AWAY_AFTER_MS, win = window, doc = document, onResume = () => {},
} = {}) {
  let idle = false;
  let lastInput = 0;
  let timer = null;
  let awayTimer = null;
  const visible = () => doc.visibilityState !== 'hidden';
  const report = () => {
    const previous = attention;
    attention = !visible() ? 'hidden' : idle ? 'idle' : 'active';
    setFocused(attention === 'active');
    if ((previous === 'hidden' && attention !== 'hidden') || (previous === 'idle' && attention === 'active')) onResume();
  };
  const arm = () => {
    win.clearTimeout(timer);
    timer = win.setTimeout(() => {
      idle = true;
      report();
    }, idleAfterMs);
  };
  const onInput = () => {
    const now = Date.now();
    if (!idle && now - lastInput < INPUT_THROTTLE_MS) return;
    lastInput = now;
    arm();
    if (idle) {
      idle = false;
      report();
    }
  };
  const onVisibility = () => {
    win.clearTimeout(awayTimer);
    if (!visible()) {
      awayTimer = win.setTimeout(report, awayAfterMs);
      return;
    }
    idle = false;
    lastInput = Date.now();
    arm();
    report();
  };

  INPUT_EVENTS.forEach((type) => win.addEventListener(type, onInput, { passive: true, capture: true }));
  doc.addEventListener('visibilitychange', onVisibility);
  noteInput = onInput;
  arm();
  report();
  return () => {
    INPUT_EVENTS.forEach((type) => win.removeEventListener(type, onInput, { capture: true }));
    doc.removeEventListener('visibilitychange', onVisibility);
    win.clearTimeout(timer);
    win.clearTimeout(awayTimer);
    if (noteInput === onInput) {
      noteInput = () => {};
      attention = 'active';
    }
  };
}

// A poll that reads in the background stops when a tick finds the tab hidden or, for
// whileInUse, unattended. Focus refetches only stale queries, so restart these explicitly.
const readsInBackground = (query) => query.observers.some((observer) => observer.options.refetchIntervalInBackground);

/** Makes React Query treat an unattended workspace like a hidden tab. */
export function installAttention(client, options = {}) {
  focusManager.setEventListener((setFocused) => watchAttention(setFocused, {
    ...options,
    onResume: () => client.refetchQueries({ type: 'active', predicate: readsInBackground }, { cancelRefetch: false }),
  }));
}
